import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { Patient } from "../models/patient";
import { VerticalStack } from "./primitives/Stacks";
import { useCreatePatient, useUpdatePatient } from "../queries/patients-query";
import { useAppContext } from "../context/AppContext";
import { DialogTransition } from "./primitives/DialogTransition";

export const PatientInfoDialog: React.FC<Omit<DialogProps, "open">> = ({
  ...dialogProps
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { mutateAsync: createPatient } = useCreatePatient();
  const { mutateAsync: updatePatient } = useUpdatePatient();

  const { togglePatientDialog, patientDialog } = useAppContext();

  const [{ name }, setData] = useState<{
    name: string;
  }>({
    name: patientDialog.patient?.name || "",
  });

  const onClose = useCallback(() => {
    togglePatientDialog({ open: false });
    setData({ name: "" });
  }, [togglePatientDialog]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (patientDialog.patient) {
        updatePatient({ ...patientDialog.patient, name });
      } else {
        createPatient({ name })
          .then((patient: Patient) => {
            navigate(`/protected/patient/${encodeURIComponent(patient.id)}`);
          })
          .catch((e: unknown) => {
            console.error("Data import error: ", e);
          });
      }
      onClose();
    },
    [
      createPatient,
      name,
      navigate,
      onClose,
      patientDialog.patient,
      updatePatient,
    ]
  );

  useEffect(() => {
    setData((d) => ({
      ...d,
      name: patientDialog.patient?.name || "",
    }));
  }, [patientDialog]);

  const hasPatient = Boolean(patientDialog.patient);

  return (
    <Dialog
      TransitionComponent={DialogTransition}
      onClose={onClose}
      open={patientDialog.open}
      {...dialogProps}
    >
      <AppBar elevation={0} sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ mr: 2, flex: 1 }} variant="h6" component="div">
            {t(hasPatient ? "patient.edit-title" : "patient.create-title")}
          </Typography>
          <Tooltip title={t("common.close")}>
            <IconButton
              aria-label={`${t("common.close")}`}
              color="inherit"
              edge="end"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <VerticalStack>
          <form onSubmit={onSubmit}>
            <TextField
              inputProps={{ autoComplete: "off" }}
              label={t("patient.name-label")}
              margin="dense"
              onChange={(e) =>
                setData((d) => ({ ...d, name: e.target.value || "" }))
              }
              required
              value={name}
            />
            <Button
              disabled={!name}
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
            >
              {t(hasPatient ? "patient.edit-submit" : "patient.create-submit")}
            </Button>
          </form>
          <Button fullWidth onClick={onClose} variant="text">
            {t("common.cancel")}
          </Button>
        </VerticalStack>
      </DialogContent>
    </Dialog>
  );
};
